import { MarketLocation, useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { isDirectMarketing } from '@e2m-customer-portal/shared';
import {
  calendarWeekFormat,
  DateTimePicker,
  DeviceMenuItem,
  MultiSelect,
  SelectItemType,
} from '@e2m-customer-portal/ui';
import { IconButton, Tooltip } from '@mui/material';
import { add, startOfDay, startOfISOWeek, toDate } from 'date-fns';
import { ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, ArrowRightIcon } from '../../../assets/icons';
import { DeviceWithInstalledPower } from '../control-energy-forecast/ControlEnergyForecast';
import clsx from 'clsx';

interface SelectedDeviceCountProps {
  options: readonly SelectItemType[];
  selected: readonly SelectItemType[];
}

function SelectedDeviceCount({
  options,
  selected,
}: Readonly<SelectedDeviceCountProps>): ReactElement<SelectedDeviceCountProps> {
  const [t] = useTranslation();
  return <>{t('MARKET_LOCATION_FORECAST_DEVICE_SELECT_COUNT', { selected: selected.length, count: options.length })}</>;
}

export interface ForecastVisualizationControlProps {
  /**
   * The marketLocation to use for display.
   */
  marketLocation: MarketLocation;

  /**
   * All available devices.
   */
  availableDevices: DeviceWithInstalledPower[];

  /**
   * The devices to display.
   */
  devices: DeviceWithInstalledPower[];

  /**
   * Gets triggered when the devices selection changes.
   * @param devices
   */
  onDevicesChange?: (devices: DeviceWithInstalledPower[]) => void;

  /**
   * Whether to show the device selector. Defaults to true.
   */
  showDevices?: boolean;

  /**
   * The date to use for the visualization.
   */
  date: number | Date;

  /**
   * How many days should be selected at once. Defaults to 7.
   */
  days?: number;

  /**
   * Gets triggered when the date changes.
   * @param interval
   */
  onDateChange?: (date: Date) => void;

  /**
   * Whether to show the date select input. Defaults to true.
   */
  showDate?: boolean;

  /**
   * Flag for editing status of the short time forecast table
   */
  isEditing?: boolean;
  /**
   * Flag for hide/show elements on smartphones or tablets
   */
  isMobile?: boolean;
  /**
   * Children to render to the left of the control.
   */
  children?: ReactNode;
}

export function ForecastVisualizationControl({
  marketLocation,
  availableDevices,
  devices,
  onDevicesChange,
  date: initialDate,
  days = 7,
  onDateChange,
  showDevices = true,
  showDate = true,
  isEditing,
  isMobile,
  children,
}: Readonly<ForecastVisualizationControlProps>): ReactElement<ForecastVisualizationControlProps> {
  const [t] = useTranslation();
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();
  const [date, setDate] = useState<Date>(toDate(initialDate));

  const handleDateChange = (nextDate: Date | undefined): void => {
    if (nextDate && onDateChange) {
      onDateChange(nextDate);
    }
  };

  const initialInterval = { start: startOfDay(initialDate), end: add(startOfDay(initialDate), { days: days - 1 }) };
  const selected = { start: startOfDay(date), end: add(startOfDay(date), { days: days - 1 }) };

  return (
    <div className="border-b-divider flex flex-col flex-nowrap justify-between gap-2 border-0 border-b border-solid px-4 py-3 sm:flex-row md:flex-wrap xl:flex-nowrap">
      <div className="flex w-full flex-col items-center justify-between gap-3 sm:w-auto lg:flex-row lg:justify-center">
        {showDate && (
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Tooltip title={t('DATE_NAVIGATOR_BACK', { days })}>
              <IconButton
                disabled={isEditing}
                aria-label={t('DATE_NAVIGATOR_BACK', { days })}
                color="primary"
                onClick={() => {
                  setDate(add(date, { days: -days }));
                  handleDateChange(add(date, { days: -days }));
                }}
              >
                <ArrowLeftIcon className="size-9 rounded border border-solid border-green-200 sm:size-auto" />
              </IconButton>
            </Tooltip>
            <div className="w-full sm:w-[30ch]">
              <DateTimePicker
                label={t('MARKET_LOCATION_FORECAST_START_DATE')}
                small
                disabled={isEditing}
                onSelect={() => {
                  handleDateChange(startOfDay(selected.start));
                }}
                value={date}
                textFieldProps={{
                  value: calendarWeekFormat(
                    initialInterval.start,
                    { locale, weekStartsOn: startOfWeek },
                    initialInterval.end,
                  ),
                }}
                pickerProps={{
                  mode: 'range',
                  modifiers: {
                    'range-start': selected.start,
                    'range-end': selected.end,
                    'in-the-middle': [
                      {
                        after: selected.start,
                        before: selected.end,
                      },
                    ],
                  },
                  dateOnly: true,
                  fixedWeeks: true,
                  showWeekNumber: true,
                  showOutsideDays: true,
                  locale,
                  startOfWeek,
                  selected: { from: selected.start, to: selected.end },
                  labels: {
                    labelNext: () => t('DATE_TIME_PICKER_NEXT_MONTH'),
                    labelPrevious: () => t('DATE_TIME_PICKER_PREVIOUS_MONTH'),
                  },
                  onWeekNumberClick: (weekNumber: number, dates: Date[]) => {
                    setDate(dates[0]);
                  },
                  onDayClick: (clickedDate: Date) => {
                    if (isMobile) {
                      setDate(startOfISOWeek(clickedDate));
                    } else {
                      setDate(clickedDate);
                    }
                  },
                }}
              />
            </div>
            <Tooltip title={t('DATE_NAVIGATOR_FORWARD', { days })}>
              <IconButton
                disabled={isEditing}
                aria-label={t('DATE_NAVIGATOR_FORWARD', { days })}
                color="primary"
                onClick={() => {
                  setDate(add(date, { days }));
                  handleDateChange(add(date, { days }));
                }}
              >
                <ArrowRightIcon className="size-9 rounded border border-solid border-green-200 sm:size-auto" />
              </IconButton>
            </Tooltip>
          </div>
        )}

        {showDevices && availableDevices.length > 1 && !isMobile && (
          <MultiSelect
            disabled={isEditing}
            color="primary"
            label={t('SHARED_TECHNICAL_UNITS_INPUT_LABEL')}
            fullWidth
            options={availableDevices}
            value={devices}
            onChange={onDevicesChange}
            className="lg:w-[22ch]"
            ItemComponent={DeviceMenuItem}
            SelectComponent={SelectedDeviceCount}
          />
        )}
      </div>

      {children && marketLocation.products.some((product) => isDirectMarketing(product)) && (
        <div
          className={clsx(
            'flex grow flex-wrap justify-between gap-2 sm:flex-col sm:items-start md:flex-row md:items-end lg:items-start',
            availableDevices.length === 1 && !isMobile && 'border-l-divider border-solid border-0 md:border-l md:pl-2',
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
